import { CircleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface FieldProps {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}

export const fieldIds = (id: string) => ({ hint: `${id}-hint`, error: `${id}-error` });

/** Label above, control, hint below, error in red with an icon (design system "Fields"). */
export function Field({ id, label, hint, error, optional, className, children }: FieldProps) {
  const ids = fieldIds(id);
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <label htmlFor={id} className="font-semibold text-ink text-sm">
        {label}
        {optional && <span className="font-normal text-ink-3"> (optional)</span>}
      </label>
      {children}
      {hint && !error && (
        <p id={ids.hint} className="text-ink-3 text-xs">
          {hint}
        </p>
      )}
      {error && (
        <p id={ids.error} className="flex items-start gap-1.5 text-danger-fg text-xs">
          <CircleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
          {error}
        </p>
      )}
    </div>
  );
}

export const controlClass = cn(
  "h-11 w-full min-w-0 rounded-md border border-edge-strong bg-surface px-3 text-ink md:h-[38px]",
  "placeholder:text-ink-3/80 hover:border-slate",
  "transition-[border-color,box-shadow] duration-150 ease-out",
  "focus:border-brand focus:shadow-[0_0_0_3px_var(--color-ring)] focus:outline-none",
  "aria-invalid:border-danger aria-invalid:focus:shadow-[0_0_0_3px_rgb(220_38_38/0.25)]",
  "disabled:bg-surface-2 disabled:text-ink-2",
);

export function describedBy(id: string, hint: unknown, error: unknown): string | undefined {
  const ids = fieldIds(id);
  if (error) return ids.error;
  if (hint) return ids.hint;
  return undefined;
}
